import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Printer, Tag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  fetchOrderLabelPreview,
  generateLabelBatchPdf,
  type LabelGenerateInput,
  type LabelWeightUnit,
  type OrderLabelPreview,
} from "@/lib/labelsApi";
import { getApiErrorMessage } from "@/lib/api";

interface OrderLabelsDialogProps {
  open: boolean;
  orderId: string | null;
  onClose: () => void;
}

interface Row {
  productId: string;
  productName: string;
  unit: string;
  orderQuantity: number;
  labelCount: string;
  netWeightGrams: string;
  weightUnit: LabelWeightUnit;
  portionSizeGrams: string;
  lote: string;
  fabricadoEm: string;
  validoAte: string;
}

const numOrNull = (value: string): number | null => {
  const n = Number(value.replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

function toRow(item: OrderLabelPreview["items"][number]): Row {
  return {
    productId: item.productId,
    productName: item.productName,
    unit: item.unit,
    orderQuantity: item.orderQuantity,
    labelCount: String(item.suggestedLabelCount ?? 1),
    netWeightGrams: item.netWeightGrams != null ? String(item.netWeightGrams) : "",
    weightUnit: "g",
    portionSizeGrams: item.portionSizeGrams != null ? String(item.portionSizeGrams) : "",
    lote: item.lote ?? "",
    fabricadoEm: item.fabricadoEm ?? "",
    validoAte: item.validoAte ?? "",
  };
}

/** Campos obrigatorios para uma linha poder gerar etiqueta. */
function rowErrors(row: Row): Set<string> {
  const errs = new Set<string>();
  const count = numOrNull(row.labelCount);
  if (count == null || count < 1) errs.add("labelCount");
  const weight = numOrNull(row.netWeightGrams);
  if (row.netWeightGrams.trim() === "" || weight == null || weight <= 0) errs.add("netWeightGrams");
  if (!row.lote.trim()) errs.add("lote");
  if (!row.fabricadoEm) errs.add("fabricadoEm");
  if (!row.validoAte) errs.add("validoAte");
  return errs;
}

export function OrderLabelsDialog({ open, orderId, onClose }: OrderLabelsDialogProps) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<OrderLabelPreview | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !orderId) return;
    let cancelled = false;
    setLoading(true);
    setPreview(null);
    setRows([]);
    fetchOrderLabelPreview(orderId)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        setRows(data.items.map(toRow));
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(getApiErrorMessage(err, "Não foi possível carregar as etiquetas do pedido"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orderId]);

  const update = (index: number, patch: Partial<Row>) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (index: number) => {
    setRows((current) => current.filter((_, i) => i !== index));
  };

  const errorsByRow = useMemo(() => rows.map(rowErrors), [rows]);
  const totalLabels = useMemo(
    () => rows.reduce((sum, row) => sum + Math.max(0, numOrNull(row.labelCount) ?? 0), 0),
    [rows],
  );
  const invalidCount = errorsByRow.filter((e) => e.size > 0).length;
  const canGenerate = rows.length > 0 && invalidCount === 0 && totalLabels > 0;

  const handleGenerate = async () => {
    if (!canGenerate) {
      toast.error("Há campos obrigatórios em vermelho para preencher antes de gerar.");
      return;
    }
    const labels: LabelGenerateInput[] = rows.map((row) => ({
      productId: row.productId,
      lote: row.lote.trim() || undefined,
      fabricadoEm: row.fabricadoEm || undefined,
      validoAte: row.validoAte || undefined,
      netWeightGrams: numOrNull(row.netWeightGrams) ?? undefined,
      netWeightUnit: row.weightUnit,
      portionSizeGrams: numOrNull(row.portionSizeGrams) ?? undefined,
      quantity: numOrNull(row.labelCount) ?? 1,
    }));
    setSubmitting(true);
    try {
      await generateLabelBatchPdf(labels);
      toast.success(`Etiquetas geradas (${totalLabels})`);
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Falha ao gerar as etiquetas"));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (rowIdx: number, field: string) =>
    cn("h-8 min-w-0 px-2", errorsByRow[rowIdx]?.has(field) && "border-destructive ring-1 ring-destructive/40");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Gerar etiquetas
            {preview ? (
              <span className="text-sm font-normal text-muted-foreground">
                · Pedido #{preview.orderNumber} · {preview.clientName}
              </span>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> Carregando itens…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            Nenhum item neste pedido para gerar etiqueta.
          </div>
        ) : (
          <>
            {invalidCount > 0 && (
              <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {invalidCount} {invalidCount === 1 ? "item precisa" : "itens precisam"} de campos obrigatórios
                (em vermelho) antes de gerar.
              </div>
            )}
            <div className="max-h-[60vh] overflow-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-surface-elevated text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Produto</th>
                    <th className="px-2 py-2 text-left w-24">Etiquetas</th>
                    <th className="px-2 py-2 text-left w-44">Peso / unid.</th>
                    <th className="px-2 py-2 text-left w-32">Lote</th>
                    <th className="px-2 py-2 text-left w-36">Fabricado</th>
                    <th className="px-2 py-2 text-left w-36">Válido até</th>
                    <th className="px-2 py-2 text-left w-24">Porção</th>
                    <th className="px-2 py-2 w-10" aria-label="Remover" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={`${row.productId}-${i}`} className="border-t border-border align-top">
                      <td className="px-3 py-2">
                        <div className="font-display font-semibold uppercase tracking-wide">{row.productName}</div>
                        <div className="text-xs text-muted-foreground">
                          Pedido: {row.orderQuantity} {row.unit}
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          type="number"
                          min={1}
                          value={row.labelCount}
                          onChange={(e) => update(i, { labelCount: e.target.value })}
                          className={inputCls(i, "labelCount")}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min={0}
                            value={row.netWeightGrams}
                            onChange={(e) => update(i, { netWeightGrams: e.target.value })}
                            placeholder="—"
                            className={inputCls(i, "netWeightGrams")}
                          />
                          <Select
                            value={row.weightUnit}
                            onValueChange={(v) => update(i, { weightUnit: v as LabelWeightUnit })}
                          >
                            <SelectTrigger className="h-8 w-16 shrink-0 px-2" aria-label="Unidade do peso">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="g">g</SelectItem>
                              <SelectItem value="kg">kg</SelectItem>
                              <SelectItem value="ml">ml</SelectItem>
                              <SelectItem value="un">un</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          value={row.lote}
                          onChange={(e) => update(i, { lote: e.target.value })}
                          className={inputCls(i, "lote")}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <DatePicker
                          value={row.fabricadoEm}
                          onChange={(next) => update(i, { fabricadoEm: next })}
                          className={inputCls(i, "fabricadoEm")}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <DatePicker
                          value={row.validoAte}
                          onChange={(next) => update(i, { validoAte: next })}
                          className={inputCls(i, "validoAte")}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          type="number"
                          min={0}
                          value={row.portionSizeGrams}
                          onChange={(e) => update(i, { portionSizeGrams: e.target.value })}
                          placeholder="—"
                          className="h-8 min-w-0 px-2"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => removeRow(i)}
                          title="Remover este item (não gerar etiqueta)"
                          aria-label={`Remover ${row.productName}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DialogFooter className="items-center justify-between gap-2 sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {rows.length > 0 ? `${totalLabels} etiqueta${totalLabels === 1 ? "" : "s"} no total` : ""}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button onClick={handleGenerate} disabled={!canGenerate || submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              Gerar etiquetas{totalLabels > 0 ? ` (${totalLabels})` : ""}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
